import { ref, watch, onUnmounted, toRefs } from 'vue';

// utils
import get from 'lodash.get';
import { isString, isScrollWithinElementRange } from '~/utils';
import { getElementOffset } from '~/utils/getElementOffset';

// composables
import { useHeaderOffset } from '~/composables/useHeaderOffset';

import type { ContentTableNavItem } from '~/types';

export function useActiveSectionObserver(navItems: Ref<ContentTableNavItem[]>) {
  const activeId = ref<null | string>(null);

  const { headerOffset } = useHeaderOffset();

  const scrollHandler = () => {
    const navItem = navItems.value.find(item =>
      isScrollWithinElementRange(toRefs(item), headerOffset.value),
    );

    activeId.value = get(navItem, 'href', null);
  };

  if (import.meta.client) {
    watch(
      [navItems, headerOffset],
      ([items, headerOffsetValue]) => {
        if (!items || !items.length || !headerOffsetValue) return;

        items.forEach((item, index) => {
          if (!isString(item.href) || !isString(item.title)) return;

          const el = document.getElementById(item.href.replace('#', ''));

          if (!el) return;

          const currentDistanceFromTop =
            getElementOffset(el, 'top') - headerOffsetValue;
          const prevItem = items[index - 1];

          if (prevItem) {
            const prevDistanceFromTop = prevItem.distanceFromTop;

            if (typeof prevDistanceFromTop === 'number') {
              prevItem.height = currentDistanceFromTop - prevDistanceFromTop;
            }
          }

          // Infinity means that last item always will be active if scroll at the bottom of the page
          navItems.value[index].height =
            index === items.length - 1 ? Infinity : null;
          navItems.value[index].distanceFromTop = currentDistanceFromTop;
        });

        document.addEventListener('scroll', scrollHandler, { passive: true });
      },
      {
        flush: 'post',
        immediate: true,
      },
    );
  }

  onUnmounted(() => document.removeEventListener('scroll', scrollHandler));

  return { activeId };
}
